/**
 * An awaitable sleep function.
 *
 * @param time The length of time to sleep for in milliseconds.
 * @returns A promise that resolves after at least `time` time
 */
export function sleep(time: number) {
  const promise = new Promise((resolve) => {
    window.setTimeout(resolve, time);
  });
  return promise;
}

/**
 * Calculates the duration between a given date/time string and the current time,
 * and formats it into a human-readable, relative time string (e.g., "5 min ago").
 *
 * If the date is in the future, it returns the full local date and time string.
 *
 * @param {string} datetimeString - A date and time string that can be parsed by the Date constructor.
 * @returns {string} A formatted relative duration string (e.g., "1 day 5 hr 10 min ago", "just now"),
 * the local date/time if in the future, or "Invalid Date".
 */
export function formatDuration(datetimeString: string): string {
  const pastDate = new Date(datetimeString);
  const now = new Date();

  // invalid date
  if (isNaN(pastDate.getTime())) {
    return "Invalid Date";
  }
  // future date
  if (pastDate.getTime() > now.getTime()) {
    return pastDate.toLocaleDateString() + " " + pastDate.toLocaleTimeString();
  }

  let diffMs = now.getTime() - pastDate.getTime();

  // Constants for time conversion
  const MS_IN_SECOND = 1000;
  const MS_IN_MINUTE = MS_IN_SECOND * 60;
  const MS_IN_HOUR = MS_IN_MINUTE * 60;
  const MS_IN_DAY = MS_IN_HOUR * 24;

  const parts: string[] = [];

  const days = Math.floor(diffMs / MS_IN_DAY);
  if (days > 0) {
    parts.push(`${days} day`);
    diffMs -= days * MS_IN_DAY;
  }

  const hours = Math.floor(diffMs / MS_IN_HOUR);
  if (hours > 0 || parts.length > 0) {
    parts.push(`${hours} hr`);
    diffMs -= hours * MS_IN_HOUR;
  }

  const minutes = Math.floor(diffMs / MS_IN_MINUTE);
  if (minutes > 0 || parts.length > 0) {
    parts.push(`${minutes} min`);
    diffMs -= minutes * MS_IN_MINUTE;
  }

  const seconds = Math.floor(diffMs / MS_IN_SECOND);
  if (parts.length === 0 && seconds > 0) {
    parts.push(`${seconds} sec`);
  }

  if (parts.length === 0) {
    return "just now";
  }

  const formatted = parts.slice(0, 3).join(" ");

  return `${formatted} ago`;
}
